import { useCallback, useMemo, useState } from 'react';

import useAuth from 'portal/src/auth/useAuth';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import { LayoutPreferenceContext } from './LayoutPreferenceContext';
import {
  getStoredLayoutPreference,
  StoredLayoutPreference,
} from './layoutPreferenceUtils';

const LAYOUT_PREFERENCE_STORAGE_KEY = 'layoutPreference';
const LEGACY_TABLET_MENU_OPEN_STORAGE_KEY = 'tabletMenuOpen';
const LEGACY_TABLET_MENU_ITEM_STORAGE_KEY = 'tabletMenuItemId';

/** @deprecated Temporary dual-layout rollout provider. Remove when legacy layout is retired. */
export const LayoutPreferenceProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const snackbarHandler = useSnackbarHandler();
  const { getUser } = useAuth();
  const [layoutPreference, setLayoutPreference] =
    useState<StoredLayoutPreference>(getStoredLayoutPreference);

  const persistLayoutPreference = useCallback(
    (nextPreference: StoredLayoutPreference) => {
      setLayoutPreference(nextPreference);
      localStorage.setItem(
        LAYOUT_PREFERENCE_STORAGE_KEY,
        JSON.stringify(nextPreference),
      );
      localStorage.removeItem(LEGACY_TABLET_MENU_OPEN_STORAGE_KEY);
      localStorage.removeItem(LEGACY_TABLET_MENU_ITEM_STORAGE_KEY);
    },
    [],
  );

  const setUseNewLayout = useCallback(
    (nextValue: boolean) => {
      persistLayoutPreference({
        ...layoutPreference,
        useNewLayout: nextValue,
      });
      const user = getUser();

      if (window.pendo && user?.id && user.organizationId) {
        pendo.identify({
          visitor: {
            id: user.id,
            layoutVersion: nextValue ? 'new' : 'legacy',
          },
          account: {
            id: user.organizationId,
          },
        });
      }
      snackbarHandler.success('Layout preference updated');
    },
    [getUser, layoutPreference, persistLayoutPreference, snackbarHandler],
  );

  const setIsMenuPinnedOpen = useCallback(
    (nextValue: boolean) => {
      persistLayoutPreference({
        ...layoutPreference,
        isMenuPinnedOpen: nextValue,
      });
    },
    [layoutPreference, persistLayoutPreference],
  );

  const setIsTabletMenuOpen = useCallback(
    (nextValue: boolean) => {
      persistLayoutPreference({
        ...layoutPreference,
        isTabletMenuOpen: nextValue,
      });
    },
    [layoutPreference, persistLayoutPreference],
  );

  const setTabletMenuItemId = useCallback(
    (nextValue: string | null) => {
      persistLayoutPreference({
        ...layoutPreference,
        tabletMenuItemId: nextValue,
      });
    },
    [layoutPreference, persistLayoutPreference],
  );

  const value = useMemo(
    () => ({
      isMenuPinnedOpen: layoutPreference.isMenuPinnedOpen,
      isTabletMenuOpen: layoutPreference.isTabletMenuOpen,
      setIsMenuPinnedOpen,
      setIsTabletMenuOpen,
      setTabletMenuItemId,
      useNewLayout: layoutPreference.useNewLayout,
      tabletMenuItemId: layoutPreference.tabletMenuItemId,
      setUseNewLayout,
    }),
    [
      layoutPreference.isMenuPinnedOpen,
      layoutPreference.isTabletMenuOpen,
      layoutPreference.tabletMenuItemId,
      layoutPreference.useNewLayout,
      setIsMenuPinnedOpen,
      setIsTabletMenuOpen,
      setTabletMenuItemId,
      setUseNewLayout,
    ],
  );

  return (
    <LayoutPreferenceContext.Provider value={value}>
      {children}
    </LayoutPreferenceContext.Provider>
  );
};
